import * as React from 'react'

import {
  HorizontalAlignment,
  i18n,
  useSiteConfig,
  useTheme,
} from '@thg-commerce/enterprise-core'
import {
  ProductBlockProps,
  ProductBlock,
} from '@thg-commerce/enterprise-components'
import { mq, spacing, styled, Text } from '@thg-commerce/enterprise-theme'
import {
  Carousel,
  CarouselButtonPlacement,
} from '@thg-commerce/gravity-patterns'
import { PictureProps } from '@thg-commerce/gravity-system'

export interface ProductProps extends Omit<ProductBlockProps, 'review'> {
  url: string
  image: {
    urls: {
      largeProduct: string
    }
    altText: string
    isAmp: boolean
    lazy?: boolean
  }
  review?: {
    starRating: number
    numberOfReviews: number
  } | null
  picture?: PictureProps
  hoverPicture?: PictureProps
}

export interface BestSellersProps {
  title?: {
    text: string
    horizontalAlignment?: HorizontalAlignment
  }
  shouldDisplayTitle?: boolean
  subtitle?: {
    text: string
    marginBottom?: number
    horizontalAlignment?: HorizontalAlignment
  }
  productBlocks: ProductProps[]
  className?: string
  widgetIndex: number
}

const Title = styled.h2<{ horizontalAlignment?: HorizontalAlignment }>`
  ${(props) =>
    Text(
      props.theme.widget.bestSellers.title.font.entry,
      props.theme.widget.bestSellers.title.font.style,
    )};
  text-transform: ${(props) =>
    props.theme.widget.bestSellers.title.font.transform};
  color: ${(props) => props.theme.widget.bestSellers.title.font.textColor};
  text-decoration: ${(props) =>
    props.theme.widget.bestSellers.title.font.textDecoration};
  text-align: ${(props) => props.horizontalAlignment || 'center'};
  margin-top: ${spacing(3)};
`

const SubTitle = styled.h2<{ horizontalAlignment?: HorizontalAlignment }>`
  ${(props) =>
    Text(
      props.theme.widget.bestSellers.subtitle.font.entry,
      props.theme.widget.bestSellers.subtitle.font.style,
    )};
  text-align: ${(props) => props.horizontalAlignment || 'center'};
  margin-top: ${spacing(1)};
`

const CarouselContainer = styled.div`
  margin-top: ${spacing(3)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    margin-top: ${spacing(4)};
  }
`

export const BestSellers = (props: BestSellersProps) => {
  const theme = useTheme()
  const i18nText = {
    carouselText: {
      leftScrollLabel: i18n('carousel.controls.left.arialabel'),
      rightScrollLabel: i18n('carousel.controls.right.arialabel'),
    },
    buyAriaLabel: i18n('general.productbuynow.label'),
    quickBuyText: i18n('general.product.quickbuy.text'),
    buyText: i18n('general.productbuynow.text'),
    reviewsStarsLabel: i18n('reviews.stars.label'),
    reviewsLabel: i18n('reviews.reviews.label'),
  }

  const { hideProductListProductBlockButton } = useSiteConfig()

  const screenReaderReviewText = ({ score, total }) =>
    `${score} ${i18nText.reviewsStarsLabel} ${total} ${i18nText.reviewsLabel}`

  const productBlocks = props.productBlocks
    .filter((product) => product.sku)
    .map((product, index) => (
      <ProductBlock
        key={index}
        {...product}
        image={{
          ...product.image,
          lazy: props.widgetIndex > 1 ? true : product.image.lazy,
        }}
        picture={
          product.picture?.sources
            ? {
                ...product.picture,
                lazy: props.widgetIndex > 1 ? true : product.image.lazy,
              }
            : undefined
        }
        review={
          product.review
            ? {
                ...product.review,
                screenReaderOnlyText: screenReaderReviewText({
                  score: product.review?.starRating,
                  total: product.review?.numberOfReviews,
                }),
              }
            : undefined
        }
        button={
          !hideProductListProductBlockButton
            ? {
                title: i18nText.buyText,
                ariaLabel: i18nText.buyAriaLabel,
                quickBuyTitle: i18nText.quickBuyText,
              }
            : undefined
        }
      />
    ))

  const carouselBreakpoints = {
    2: [2, 2, 2, 2],
    3: [1, 2, 3, 3],
    default: [1, 2, 4, 4],
  }

  if (props.productBlocks.length === 0) return null

  return (
    <div className={props.className}>
      {props.title?.text && props.title.text.length > 2 ? (
        <Title horizontalAlignment={props.title?.horizontalAlignment}>
          {props.title.text}
        </Title>
      ) : null}
      {props.subtitle && (
        <SubTitle horizontalAlignment={props.subtitle.horizontalAlignment}>
          {props.subtitle.text}
        </SubTitle>
      )}
      <CarouselContainer>
        <Carousel
          items={productBlocks}
          i18n={i18nText.carouselText}
          itemsPerSlide={
            carouselBreakpoints[productBlocks.length] ||
            carouselBreakpoints.default
          }
          hideControlsOnSingleSlide={true}
          inactiveSlideFullOpacity={true}
          buttonPlacement={CarouselButtonPlacement.Split}
          indicatorStyle={theme.widget.bestSellers.carousel.indicatorStyle}
          enableSlidePreview={true}
        />
      </CarouselContainer>
    </div>
  )
}
